import { RightOutlined } from '@ant-design/icons'
import type { Ratebook } from '@api/useFreight/types'
import { BBDTag, GraviButton, Texto } from '@gravitate-js/excalibrr'
import type { IDoesFilterPassParams, IFilterParams } from 'ag-grid-community'
import { Badge, Divider, Radio, Space } from 'antd'
import moment from 'moment'
import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { ProductMinimum } from './types'

export function StatusRenderer({
  value,
}: {
  value: Ratebook['invoice_status']
}) {
  switch (value) {
    case 'Active':
      return <BBDTag theme2>{value}</BBDTag>
    case 'Hold':
      return <BBDTag warning>{value}</BBDTag>
    default:
      return <BBDTag>{value}</BBDTag>
  }
}

// the labal counterparty_id was commented out, will show address in future?
export function CustomerRenderer({ data }: { data: Ratebook }) {
  // Format address from address_info if available
  const formatAddressForDisplay = () => {
    // First try to use structured address_info
    if (data.address_info) {
      const { address, city, state, postal_code } = data.address_info
      const parts = []

      if (address) parts.push(address)
      if (city && state) parts.push(`${city}, ${state}`)
      else if (city) parts.push(city)
      else if (state) parts.push(state)
      if (postal_code) parts.push(postal_code)

      if (parts.length > 0) {
        return parts.join(', ')
      }
    }

    // Fall back to simple address field or empty string
    return data.address || ''
  }

  return (
    <div>
      <Texto category='p1' weight='600'>
        {data.counterparty_name}
      </Texto>
      <Texto category='label'>{formatAddressForDisplay()}</Texto>
    </div>
  )
}

export function DateRenderer({ value }: { value: string | null }) {
  if (!value) return <Texto appearance='hint'>N/A</Texto>
  return <Texto>{moment(value).format('MM/DD/YY')}</Texto>
}

export const freightMethodMap = {
  longest_leg: 'Longest Leg',
  sum_of_legs: 'Sum All Legs',
  by_lane: 'By Lane',
  highest_rate: 'Highest Rate',
} as const

export function SplitLoadRenderer({
  value,
}: {
  value: keyof typeof freightMethodMap
}) {
  return freightMethodMap[value] || value
}

export function ProductMinimumsRenderer({
  value,
}: {
  value: ProductMinimum[]
}) {
  const getProductMinimum = (productGroup: string) => {
    return value?.find((min) => min.productGroup.toUpperCase() === productGroup)
  }

  const renderRow = (productGroup: string) => {
    const minimum = getProductMinimum(productGroup)
    const hasVolume = minimum?.volume != null

    return (
      <Space key={productGroup} style={{ width: '100%', justifyContent: 'flex-start' }}>
        <Badge
          status={hasVolume ? 'success' : 'default'}
          text={
            <Space size='small'>
              <Texto style={{ width: 65 }}>{productGroup}:</Texto>
              <Texto align='right' appearance={hasVolume ? 'default' : 'hint'}>
                {hasVolume ? `${minimum.volume?.toLocaleString()} gal` : 'N/A'}
              </Texto>
            </Space>
          }
        />
      </Space>
    )
  }

  return (
    <Space direction='vertical' size={2} style={{ width: '100%' }}>
      {renderRow('GAS')}
      {renderRow('DIESEL')}
    </Space>
  )
}

export function ActionRenderer({ data }: { data: Ratebook }) {
  const navigate = useNavigate()

  return (
    <GraviButton
      buttonText='Open'
      icon={<RightOutlined />}
      onClick={() => navigate(`/freight/${data.book_type === 'Revenue' ? 'ratebooks' : 'carrier_books'}/${data._id}`)}
    />
  )
}

export const invoiceGroupingMap = {
  Single: 'By Order',
  Multiple: 'By Line Item Type',
} as const
